export interface Params {
	[key: string]: string | undefined;
}

export interface Context {
	params: Params;
	path: string;
	state: {
		[key: string]: any;
	};
}

type RouteCallback = (context: Context) => Promise<void>;

interface Route {
	cb: RouteCallback;
	keys: string[];
	regex: RegExp;
}

type NonStandardEvent = (MouseEvent | TouchEvent) & {
	button: 0 | 1 | 2 | 3 | 4;
	composedPath: () => HTMLAnchorElement[];
	path: HTMLAnchorElement[];
};

type RouteMatched = (arg: {
	context: Context;
}) => void | Promise<void> | Promise<void | false>;

type NavigationEnd = (arg: { context: Context; error: Error | null }) => void;

// Switch to URLPattern when supported: Chrome 95, Firefox 142, Safari 26

const decodeURLEncodedURIComponent = (val: string) => {
	if (typeof val !== "string") {
		return val;
	}
	return decodeURIComponent(val.replaceAll("+", " "));
};

const match = (route: Route, pathname: string) => {
	const params: Params = {};
	let matches = false;

	const m = route.regex.exec(pathname);

	if (m) {
		matches = true;
		for (let i = 1, len = m.length; i < len; ++i) {
			const key = route.keys[i - 1]!;
			let val;
			try {
				val = decodeURLEncodedURIComponent(m[i]!);
			} catch {
				// This happens on some inputs to decodeURIComponent such as /user/%C2 - returning no match is better than an error
				return {
					matches: false,
					params: {},
				};
			}
			if (val !== undefined) {
				params[key] = val;
			}
		}
	}

	return { matches, params };
};

export const makeRegex = (path: string) => {
	const parts = path
		.replace(/(^\/+|\/+$)/g, "") // Strip starting and ending slashes
		.split("/");

	const keys = [];
	let regexString = "^";
	for (const part of parts) {
		if (part.startsWith(":")) {
			keys.push(part.slice(1));
			regexString += "/([^/]+?)";
		} else {
			regexString += `/${part}`;
		}
	}
	regexString += "$";

	return {
		keys,
		regex: new RegExp(regexString),
	};
};

const findAnchor = (
	e: NonStandardEvent,
): HTMLAnchorElement | SVGAElement | undefined => {
	// Find link element
	let el = e.target as HTMLElement | SVGElement;
	const eventPath = e.path || (e.composedPath ? e.composedPath() : null);
	if (eventPath) {
		for (const eventTarget of eventPath) {
			if (!eventTarget.nodeName) {
				continue;
			}
			if (eventTarget.nodeName.toUpperCase() !== "A") {
				continue;
			}
			if (!eventTarget.href) {
				continue;
			}

			el = eventTarget;
			break;
		}
	}

	// Fallback if the eventPath stuff didn't do anything (cross browser)
	while (el && el.nodeName.toUpperCase() !== "A") {
		el = el.parentNode as HTMLAnchorElement | SVGAElement;
	}
	if (!el || el.nodeName.toUpperCase() !== "A") {
		return;
	}

	return el as HTMLAnchorElement | SVGAElement;
};

const sameOrigin = (href: string) => {
	if (!href) {
		return false;
	}

	const url = new URL(href, window.location.toString());

	return (
		window.location.protocol === url.protocol &&
		window.location.hostname === url.hostname &&
		window.location.port === url.port
	);
};

const samePath = (url: HTMLAnchorElement) => {
	return (
		url.pathname === window.location.pathname &&
		url.search === window.location.search
	);
};

const clickEvent = document.ontouchstart ? "touchstart" : "click";

class Router {
	private routeMatched: RouteMatched | undefined;
	private navigationEnd: NavigationEnd | undefined;
	private routes: Route[];
	private lastNavigatedPath: string | undefined;
	public shouldBlock:
		| ((refresh: boolean) => boolean | Promise<boolean>)
		| undefined;

	constructor() {
		this.routes = [];

		window.addEventListener("beforeunload", (event) => {
			// Only show prompt when needed
			if (this.shouldBlock) {
				// Cancel the event
				event.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
				// Chrome requires returnValue to be set
				event.returnValue = "";
			}
		});
	}

	public async navigate(
		path: string,
		{
			refresh = false,
			replace = false,
			state = {},
		}: {
			refresh?: boolean;
			replace?: boolean;
			state?: { [key: string]: any };
		} = {},
	) {
		const context: Context = {
			params: {},
			path,
			state,
		};
		let error: Error | null = null;

		let pathname = path;
		const queryIndex = pathname.indexOf("?");
		if (queryIndex !== -1) {
			pathname = pathname.slice(0, queryIndex);
		}
		const hashIndex = pathname.indexOf("#");
		if (hashIndex !== -1) {
			pathname = pathname.slice(0, hashIndex);
		}

		let handled = false;
		for (const route of this.routes) {
			const { matches, params } = match(route, pathname);
			if (matches) {
				context.params = params;

				try {
					if (this.shouldBlock) {
						const shouldBlock = await this.shouldBlock(refresh);
						if (shouldBlock) {
							return;
						}
					}

					if (this.routeMatched) {
						const output = await this.routeMatched({
							context,
						});
						if (output === false) {
							return;
						}
					}

					if (replace) {
						// Only do this on replace, not refresh, or Safari can complain about too many calls
						window.history.replaceState(
							{
								path,
							},
							document.title,
							path,
						);
					} else if (!refresh) {
						window.history.pushState(
							{
								path,
							},
							document.title,
							path,
						);
					}

					this.lastNavigatedPath = path;

					await route.cb(context);
				} catch (error_) {
					error = error_;
				}

				handled = true;
				break;
			}
		}

		if (!handled) {
			error = new Error("Matching route not found");
		}

		// HACK! Some ads were including a request for /ads.txt?upapi=true which somehow triggered this code and led to Controller attempting to render multiple pages at once, one of which was outside of the league, leading to beforeViewNonLeague to be called and stop game sim
		if (error && path.includes("ads.txt")) {
			return;
		}

		if (this.navigationEnd) {
			this.navigationEnd({
				context,
				error,
			});
		}
	}

	public start({
		routeMatched,
		navigationEnd,
		routes,
	}: {
		routeMatched?: RouteMatched;
		navigationEnd?: NavigationEnd;
		routes: { [key: string]: RouteCallback };
	}) {
		this.routeMatched = routeMatched;
		this.navigationEnd = navigationEnd;

		for (const [path, cb] of Object.entries(routes)) {
			const { keys, regex } = makeRegex(path);
			this.routes.push({
				cb,
				keys,
				regex,
			});
		}

		document.addEventListener(clickEvent, (e) => {
			this._onclick(e as NonStandardEvent);
		});
		window.addEventListener("popstate", (e) => {
			this._onpopstate(e);
		});

		this.navigate(location.pathname + location.search + location.hash, {
			replace: true,
		});
	}

	// Mostly taken from page.js
	private _onclick(e: NonStandardEvent) {
		if (
			e.button !== 0 ||
			e.metaKey ||
			e.ctrlKey ||
			e.shiftKey ||
			e.defaultPrevented ||
			!e.target
		) {
			return;
		}

		const anchor = findAnchor(e);
		if (!anchor) {
			return;
		}

		if (
			anchor.hasAttribute("download") ||
			anchor.getAttribute("rel") === "external"
		) {
			return;
		}

		// There are various special cases for SVGs, not everything will work right
		const svg = anchor instanceof SVGAElement;

		// ensure non-hash for the same path
		const link = anchor.getAttribute("href");
		if (!svg && samePath(anchor) && (anchor.hash || link === "#")) {
			return;
		}

		if (link && link.includes("mailto:")) {
			return;
		}

		// string check is needed otherwise links inside an SVG (like in Player Graphs) will always get caught here
		if (typeof anchor.target === "string" && anchor.target.startsWith("_")) {
			return;
		}

		if (!svg && !sameOrigin(anchor.href)) {
			return;
		}

		// rebuild path
		let path;
		if (svg) {
			// Special case for SVG links
			path = anchor.href.baseVal;
		} else {
			path = anchor.pathname + anchor.search + (anchor.hash || "");
			path = path[0] !== "/" ? `/${path}` : path;
		}

		e.preventDefault();

		this.navigate(path);
	}

	private _onpopstate(event: Event & { state: any }) {
		if (document.readyState !== "complete") {
			return;
		}

		const path =
			event.state && typeof event.state.path === "string"
				? event.state.path
				: location.pathname + location.search + location.hash;

		if (
			this.lastNavigatedPath &&
			this.lastNavigatedPath.split("#")[0] === path.split("#")[0]
		) {
			// Just switching the hash in the URL on the same page, not actually navigation
			return;
		}

		this.navigate(path, { replace: true });
	}
}

const router = new Router();

export default router;
